import { api } from '../../fetchWrapper';
import { logger } from '../../../utils/logger';
import { API_ENDPOINTS, getEndpoint } from '../../apiConstants';
import type { MailData, MailApiResponse } from './mail.types';

// GET mail enums
export async function fetchMailEnums({
  clientCode,
  searched = true,
}: {
  clientCode: string;
  searched?: boolean;
}): Promise<string[]> {
  const params: Record<string, any> = {
    searched,
  };

  const endpoint = getEndpoint(clientCode, API_ENDPOINTS.MAIL.ENUMS);
  const response = await api.get<string[]>(endpoint, params);
  return response;
}

// Standardized GET function for mail list
export async function fetchMails({
  clientCode,
  firstResult = 0,
  maxResult = 10,
  clientId,
  jobDetailId,
  mailType,
  searched = true,
}: {
  clientCode: string;
  firstResult?: number;
  maxResult?: number;
  clientId?: number;
  jobDetailId?: number;
  mailType?: string;
  searched?: boolean;
}): Promise<MailData[]> {
  const params: Record<string, any> = {
    firstResult,
    maxResult,
    searched,
  };

  if (clientId) params.clientId = clientId;
  if (jobDetailId) params.jobDetailId = jobDetailId;
  if (mailType) params.mailType = mailType;

  const endpoint = getEndpoint(clientCode, API_ENDPOINTS.MAIL.LIST);
  const response: MailApiResponse[] = await api.get<MailApiResponse[]>(endpoint, params);
  
  return response.map((mail: MailApiResponse) => ({
    lastUpdateDate: mail.lastUpdateDate?.toString() || '',
    fromMail: mail.fromMail || '',
    toMail: mail.toMail || '',
    subject: mail.subject || '',
    mailType: mail.mailType || '',
    header: mail.header || '',
  }));
}

// GET mail count
export async function fetchMailsCount({
  clientCode,
  clientId,
  jobDetailId,
  mailType,
  searched = true,
}: {
  clientCode: string;
  clientId?: number;
  jobDetailId?: number;
  mailType?: string;
  searched?: boolean;
}): Promise<number> {
  const params: Record<string, any> = {
    searched,
  };

  if (clientId) params.clientId = clientId;
  if (jobDetailId) params.jobDetailId = jobDetailId;
  if (mailType) params.mailType = mailType;

  const endpoint = getEndpoint(clientCode, API_ENDPOINTS.MAIL.COUNT);
  const response = await api.get<any>(endpoint, params);
  
  if (typeof response === 'number') {
    return response;
  } else if (response && typeof response.count === 'number') {
    return response.count;
  }
  
  logger.warn('Invalid count response:', response);
  return 0;
}

// GET mail by ID
export async function fetchMailById({
  clientCode,
  mailId,
}: {
  clientCode: string;
  mailId: number;
}): Promise<MailData> {
  const endpoint = getEndpoint(clientCode, `${API_ENDPOINTS.MAIL.LIST}/${mailId}`);
  const response: MailApiResponse = await api.get<MailApiResponse>(endpoint);
  
  return {
    lastUpdateDate: response.lastUpdateDate?.toString() || '',
    fromMail: response.fromMail || '',
    toMail: response.toMail || '',
    subject: response.subject || '',
    mailType: response.mailType || '',
    header: response.header || '',
  };
}

// CREATE mail
export async function createMail({
  clientCode,
  payload,
}: {
  clientCode: string;
  payload: any;
}): Promise<MailData> {
  const endpoint = getEndpoint(clientCode, API_ENDPOINTS.MAIL.LIST);
  const response: MailApiResponse = await api.post<MailApiResponse>(endpoint, payload);
  
  return {
    lastUpdateDate: response.lastUpdateDate?.toString() || '',
    fromMail: response.fromMail || '',
    toMail: response.toMail || '',
    subject: response.subject || '',
    mailType: response.mailType || '',
    header: response.header || '',
  };
}

// UPDATE mail
export async function updateMail({
  clientCode,
  mailId,
  payload,
}: {
  clientCode: string;
  mailId: number;
  payload: any;
}): Promise<MailData> {
  const endpoint = getEndpoint(clientCode, `${API_ENDPOINTS.MAIL.LIST}/${mailId}`);
  const response: MailApiResponse = await api.put<MailApiResponse>(endpoint, payload);
  
  return {
    lastUpdateDate: response.lastUpdateDate?.toString() || '',
    fromMail: response.fromMail || '',
    toMail: response.toMail || '',
    subject: response.subject || '',
    mailType: response.mailType || '',
    header: response.header || '',
  };
}

// DELETE mail
export async function deleteMail({
  clientCode,
  mailId,
}: {
  clientCode: string;
  mailId: number;
}): Promise<void> {
  const endpoint = getEndpoint(clientCode, `${API_ENDPOINTS.MAIL.LIST}/${mailId}`);
  return api.delete(endpoint);
}

// SEND mail
export async function sendMail({
  clientCode,
  payload,
}: {
  clientCode: string;
  payload: any;
}): Promise<any> {
  const endpoint = getEndpoint(clientCode, `${API_ENDPOINTS.MAIL.LIST}/send`);
  return api.post(endpoint, payload);
}
